import {ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, resource, Signal, WritableResource} from '@angular/core';
import {AppQuery} from '../../queries/app.query';
import {SearchQuery as ApiSearchQuery} from '../../api/models/search-query';
import {VaultSearchQueriesService} from '../../services/search/vault-search-queries/vault-search-queries.service';
import {BasicSubscribableComponent} from '../dummy-components/basic-subscribable-component';
import {TranslateModule} from '@ngx-translate/core';
import {IconComponent} from '../icon/icon.component';
import {AppIconSizeEnum} from '../../enums/app-icon-size.enum';
import {DefaultSearchTypeEnum} from '../../enums/default-search-type.enum';
import {URL_PATH_CONFIGS} from '../../../configs/url-path.config';
import {toSignal} from '@angular/core/rxjs-interop';
import {StoredSearchItem} from '../../models/search/stored-search-item';
import {SnackbarService} from '../../services/snackbar/snackbar.service';
import {SearchQueryVisibilityEnum} from '../../enums/search-query-visibility.enum';
import {NavigationService} from '../../services/navigation/navigation.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    selector: 'app-stored-searches',
    templateUrl: './stored-searches.component.html',
    styleUrls: ['./stored-searches.component.css'],
    imports: [
        TranslateModule,
        IconComponent,
    ]
})
export class StoredSearchesComponent extends BasicSubscribableComponent {
    public readonly vaultId: InputSignal<string | undefined> = input.required<string | undefined>();
    protected globalStoredSearches: Array<StoredSearchItem>;
    protected isGlobalScope: Signal<boolean>;
    protected storedSearchQueries: Signal<Array<ApiSearchQuery>>;
    protected storedSearchQueriesResource: WritableResource<Array<ApiSearchQuery> | undefined>;
    protected readonly AppIconSize = AppIconSizeEnum;
    protected readonly SearchQueryVisibilityEnum = SearchQueryVisibilityEnum;
    private readonly appQuery = inject(AppQuery);
    private readonly snackbarService = inject(SnackbarService);
    private readonly navigationService = inject(NavigationService);
    private readonly vaultSearchQueriesService = inject(VaultSearchQueriesService);

    public constructor() {
        super();
        this.isGlobalScope = toSignal(this.appQuery.isSearchTaskGlobalScope$, { initialValue: false });
        this.globalStoredSearches = this.getDefaultGlobalSearches();

        this.storedSearchQueriesResource = resource<Array<ApiSearchQuery>, { isGlobalScope: boolean, vaultId: string | undefined }>({
            params: () => {
                const isGlobalScope = this.isGlobalScope();
                const vaultId = this.vaultId();
                return {
                    isGlobalScope,
                    vaultId
                };
            },
            loader: async ({
                               params,
                               abortSignal
                           }): Promise<Array<ApiSearchQuery>> => {
                if (!params.vaultId || params.isGlobalScope) {
                    return [];
                }

                return this.fetchSearchQueries(params.vaultId, abortSignal);
            }
        });
        this.storedSearchQueries = computed(() => {
            if (this.storedSearchQueriesResource.hasValue()) {
                return this.storedSearchQueriesResource.value();
            }

            return [];
        });
    }

    protected async openDefaultGlobalSearch(storedSearchItem: StoredSearchItem): Promise<void> {
        await this.navigationService.navigateToDefaultSearch(storedSearchItem);
    }

    protected async openStoredSearch(searchQuery: ApiSearchQuery): Promise<void> {
        await this.navigationService.navigateToSearchQuery(searchQuery);
    }

    private async fetchSearchQueries(vaultId: string, abortSignal: AbortSignal): Promise<Array<ApiSearchQuery>> {
        let searchQueries: Array<ApiSearchQuery> = [];
        try {
            searchQueries = await this.vaultSearchQueriesService.fetchSearchQueriesForVault(vaultId, abortSignal);
        } catch (error) {
            this.snackbarService.showError('STORED_SEARCHES.ERROR_FETCHING_SEARCHES', error as Error);
        }

        return searchQueries;
    }

    private getDefaultGlobalSearches(): Array<StoredSearchItem> {
        return [
            {
                title: 'FAVORITES',
                svgIcon: this.ICONS.FAVORITE,
                searchType: DefaultSearchTypeEnum.FAVORITES,
                path: URL_PATH_CONFIGS.paths.favorites,
                iconClass: 'favorite-icon'
            },
            {
                title: 'NEWEST_DOCUMENTS',
                svgIcon: this.ICONS.NEWITEMS,
                searchType: DefaultSearchTypeEnum.NEWEST,
                path: URL_PATH_CONFIGS.paths.newestDocuments
            },
            {
                title: 'LAST_EDITED_DOCUMENTS',
                svgIcon: this.ICONS.LASTEDITED,
                searchType: DefaultSearchTypeEnum.LAST_EDITED,
                path: URL_PATH_CONFIGS.paths.lastEdited,
            },
        ];
    }
}
